/**
 * What a seat shows when there is no room to show cards.
 *
 * The answer to D82. A seat on the ring is positioned on a point and centred on
 * it, so its width spreads both ways — and a seat carrying a row of cards ran
 * **off the left edge at every phone width**: 53px lost at 320, 46px at 390,
 * 43px at 412. Silently, because nothing scrolled; the content was simply gone.
 *
 * Spades and Oh Hell were fine, which is the clue. Their seats hold a name and a
 * tally. Rummy's and Cheat's hold a name, a score **and cards**, and cards are
 * what does not fit.
 *
 * So below a width a seat's cards become a count. The layout vocabulary already
 * distinguishes the two — a `ZonePlacement` is a `row` or a `count` — so this is
 * a rule about when to substitute one for the other, and no game declares
 * anything new. On a phone the individual cards are unreadable at that size
 * anyway, so nothing is lost that was being read.
 *
 * The rule is a pure function so it can be tested; the hook that watches the
 * viewport is four lines around it, the same split as `countdown.ts` and
 * `use-counter-event.ts`.
 */

import type { ZonePlacementWire } from '../net/session.js';

/**
 * Where "no room" starts.
 *
 * 560px rather than a phone's 390: the ring is what runs out of room, not the
 * screen, and it is squeezed long before a tablet. Above this every game
 * measured clean; below it Rummy and Cheat clip.
 */
export const NARROW_MAX = 560;

export const NARROW_QUERY = `(max-width: ${String(NARROW_MAX)}px)`;

/**
 * The shape to draw a seat's zone as, given the room available.
 *
 * Only `row` and `fan` collapse. A `stack` is already one card wide, and
 * `backs` is a fan of face-down cards that overlap — and it is the one thing a
 * seat shows that a count would genuinely lose, because how close somebody is
 * to going out is read by looking at them rather than at a number.
 */
export function seatShape(
  shape: ZonePlacementWire['shape'],
  narrow: boolean,
): ZonePlacementWire['shape'] {
  if (!narrow) return shape;
  if (shape === 'row' || shape === 'fan') return 'count';
  return shape;
}

/**
 * How many face-down cards to fan on a seat.
 *
 * `backs` deliberately does NOT collapse to a count: how close somebody is to
 * going out is read by looking at them rather than at a number, and that is the
 * whole reason the shape exists.
 *
 * But twelve of them do not fit on a phone — Cheat deals thirteen, and its
 * seats still ran off the edge after `row` and `fan` were collapsed. So the fan
 * is capped instead of dropped: four backs still read as a fan, and the badge
 * beside them already says the exact number whenever the fan is short of it.
 */
export const BACKS_WIDE = 12;
export const BACKS_NARROW = 4;

export function backsShown(count: number, narrow: boolean): number {
  return Math.min(count, narrow ? BACKS_NARROW : BACKS_WIDE);
}

/**
 * The placement to draw with, collapsed if it has to be.
 *
 * Returns the placement itself when nothing changes, so the common case
 * allocates nothing and a re-render is not forced by a new object.
 */
export function seatPlacement(
  placement: ZonePlacementWire,
  narrow: boolean,
): ZonePlacementWire {
  const shape = seatShape(placement.shape, narrow);
  return shape === placement.shape ? placement : { ...placement, shape };
}
